import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import Form from './Form';
import WalletAddress from './WalletAddress';
import Input from './Input';
import Button from './Button';
import InputLabel from './InputLabel';

export const getCreatedAssets = async ({ address, prefixes }) => {
  const apiUrl = `https://mainnet-idx.algonode.cloud/v2/accounts/${address}/created-assets`;
  const { data } = await axios.get(apiUrl);
  const assets = data.assets;
  let next = data['next-token'];

  while (next) {
    const { data } = await axios.get(apiUrl, {
      params: {
        next,
      },
    });
    assets.push(...data.assets);
    next = data['next-token'];
  }

  const filtered = assets.filter((asset) => {
    if (!prefixes) return true;
    const prefixesArray = prefixes.split(',');
    return prefixesArray.some((prefix) => asset.params['unit-name'].startsWith(prefix));
  });

  return filtered;
};

export const getHolder = async (assetIndex) => {
  try {
    const { balances } = (await axios.get(`https://mainnet-idx.algonode.cloud/v2/assets/${assetIndex}/balances`))?.data;

    const holders = balances;

    if (!holders) {
      throw new Error(`No holders found for ${assetIndex}`);
    }

    return holders.filter((holder) => holder.amount > 0)?.[0];
  } catch (error) {
    console.error(error);
    throw get(error, 'response.data.message', `${assetIndex} Unexpected error occurred`);
  }
};

const TheSnapshotPage = () => {
  const form = useForm({
    defaultValues: {
      address: '',
    },
  });
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState([]);
  const [clipboard, setClipboard] = useState('');
  const [hasRun, setHasRun] = useState(false);

  const updateUi = (nfts) => {
    // group by holder.address
    const grouped = nfts.reduce((acc, nft) => {
      const { address } = nft?.holder;
      if (!acc[address]) {
        acc[address] = [];
      }
      acc[address].push(nft);
      return acc;
    }, {});

    const sorted = Object.keys(grouped).sort((a, b) => {
      return grouped[b].length - grouped[a].length;
    });

    const sortedGrouped = {};
    sorted.forEach((key) => {
      sortedGrouped[key] = grouped[key];
    });

    setSnapshot(sortedGrouped);

    const forClipboard = Object.keys(sortedGrouped)
      .map((key) => `${key} ${sortedGrouped[key].length}`)
      .join('\n');

    setClipboard(forClipboard);
  };

  const handleSubmit = async (args) => {
    if (loading) return;

    if (!args.address) {
      toast.error('Address is required');
      return;
    }

    try {
      setHasRun(false);
      setSnapshot([]);
      setClipboard('');
      setLoading(true);

      // if address has a comma, split it and run for each address
      let nfts = [];
      if (args.address.includes(',')) {
        const addresses = args.address.split(',');
        for (const address of addresses) {
          const created = await getCreatedAssets({ ...args, address });
          nfts = [...nfts, ...created];
        }
      } else {
        const created = await getCreatedAssets(args);
        nfts = [...nfts, ...created];
      }

      // chunk nfts into groups of 20
      const chunkSize = 100;
      const chunks = [];
      for (let i = 0; i < nfts.length; i += chunkSize) {
        chunks.push(nfts.slice(i, i + chunkSize));
      }

      let allNfts = [];
      for (const chunk of chunks) {
        for (const nft of chunk) {
          const holder = await getHolder(nft.index);
          nft.holder = holder;
        }
        allNfts = [...allNfts, ...chunk];
        updateUi(allNfts);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
      setHasRun(true);
    }
  };

  return (
    <div className='mt-10'>
      <Form onSubmit={handleSubmit} form={form} className='flex items-center flex-col justify-center gap-5'>
        <div className='flex gap-5'>
          <div>
            <InputLabel>Creator Address</InputLabel>
            <Input type='text' name='address' required fullWidth />
          </div>
          <div>
            <InputLabel>Unit Name Prefixes</InputLabel>
            <Input type='text' name='prefixes' fullWidth />
          </div>
        </div>
        <div>
          <Button loading={loading} type='submit'>
            Take Holder's Snapshot
          </Button>
        </div>
      </Form>

      {Object.keys(snapshot).length > 0 ? (
        <div className='mx-auto flex flex-col items-center justify-center mt-10'>
          <div className='flex items-center gap-10'>
            <p className='flex flex-col items-center'>
              <span className='text-gray-400 text-sm'>Total NFTs</span>
              <span className='font-bold text-xl'>{Object.values(snapshot).reduce((acc, val) => acc + val.length, 0)}</span>
            </p>
            <p className='flex flex-col items-center'>
              <span className='text-gray-400 text-sm'>Total Owners</span>
              <span className='font-bold text-xl'>{Object.keys(snapshot).length}</span>
            </p>
          </div>
          <div className='mt-10'>
            <input type='hidden' value={clipboard} onChange={({ target: { value } }) => setClipboard(value)} />
            <CopyToClipboard text={clipboard} onCopy={() => toast.success('Copied to clipboard')}>
              <Button theme='outline'>Copy list to clipboard</Button>
            </CopyToClipboard>
          </div>

          <ul className='grid grid-cols-1 gap-3 mt-10 divide-gray-700 divide-y-2'>
            {Object.keys(snapshot).map((item, index) => (
              <li key={item} className='flex items-center gap-20 md:gap-6 pt-3'>
                <p className='w-[200px] md:w-[250px] truncate text-gray-400 text-lg'>
                  <a href={`https://www.randgallery.com/algo-collection/?address=${item}`} className='hover:text-primary-500'>
                    <WalletAddress address={item} />
                  </a>
                </p>

                <p className='font-bold text-xl'>{snapshot[item].length}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        hasRun && (
          <div className='mt-10'>
            <p className='text-center'>Sorry, but there are no results.</p>
          </div>
        )
      )}
    </div>
  );
};

export default TheSnapshotPage;
